import { Accordion, AccordionSummary, AccordionDetails, Box, Button, DialogActions, DialogContent, DialogTitle, Modal, Paper, TextField, Typography, Skeleton, Tooltip } from "@mui/material";
import CurrencyTextField from "../../../../../components/shared/fields/CurrencyTextField";
import TooltipConditionalDiscount from "./TooltipConditionalDiscount";
import TooltipUnconditionalDiscount from "./TooltipUncoditionalDiscount";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState } from "react";
import { formatCurrency } from "../../../../helpers/financialHelpers";
import FiscalInfosTable from "./FiscalInfosTable";
import { postFiscalInvoiceService } from "../../../../api/invoices";
import ErrorBox from "../../../../../components/shared/boxes/ErrorBox";

const NFSeModal = ({ open, onClose, invoice, fiscalConfigData, fiscalApiError, setfiscalApiError, onSuccess }) => {
    const [serviceDescriptionValue, setServiceDescriptionValue] = useState("");
    const [serviceValue, setServiceValue] = useState("");
    const [unconditionalDiscountValue, setUnconditionalDiscountValue] = useState("");
    const [conditionalDiscountValue, setConditionalDiscountValue] = useState("");
    const [observationsValue, setObservationsValue] = useState("");
    const [loadingSendData, setLoadingSendData] = useState(false);
    const [sendFiscalDataError, setSendFiscalDataError] = useState(null);

    const modalOnClose = () => {
        onClose();
    };

    useEffect(() => {
        if (invoice?.invoice_services?.length) {
            const description = invoice.invoice_services
                .map(service => {
                    return [
                        `${service.name}:`,
                        `preço: R$ ${formatCurrency(service.unit_value)}`,
                        `quantidade: ${service.quantity}`,
                        `total: R$ ${formatCurrency(service.subtotal_value)}`
                    ].join('\n'); 
                })
                .join('\n\n');

            setServiceDescriptionValue(description);
        }

        if (invoice?.subtotal_services_value !== undefined) {
            setServiceValue(invoice.subtotal_services_value);
        }
    }, [invoice]);

    const loadingInvoiceData = !invoice;
    const errorMessage = fiscalApiError?.errorMessage || sendFiscalDataError?.errorMessage;

    const validation = {
      serviceDescriptionLimit: serviceDescriptionValue.length > 1000,
      observationsLimit: observationsValue.length > 1000,
      missingRequiredFields: !serviceDescriptionValue ||!serviceValue,
    };

    const hasErrors = validation.serviceDescriptionLimit || validation.observationsLimit;
    const isSubmitDisabled = validation.missingRequiredFields || hasErrors;
    const submitTooltip = validation.missingRequiredFields ? "Preencha os campos obrigatórios para continuar." : hasErrors ? "Corrija os erros exibidos para continuar." : "";

    const handleSendData = async () => {
        const body = {
            fiscal_service_invoice: {
                service_value: serviceValue,
                conditional_discount_value: conditionalDiscountValue,
                unconditional_discount_value: unconditionalDiscountValue,
                description: serviceDescriptionValue,
                more_info: observationsValue,
            }
        }

        try {
            setLoadingSendData(true);
            setSendFiscalDataError(null);
            setfiscalApiError?.(null);

            await postFiscalInvoiceService({ body, invoiceId: invoice.id });

            modalOnClose();
            onSuccess();
        } catch (error) {
            setSendFiscalDataError({ errorMessage: error.message });
        } finally {
            setLoadingSendData(false);
        }
    };

    return (
        <>
            <Modal open={open} onClose={modalOnClose} sx={{ height: '100vh', width: '100vw', display: 'flex', overflow: 'auto' }}>
                <Paper
                    elevation={16}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        p: '1rem',
                        borderRadius: '0.25rem',
                        maxWidth: '38rem',
                        mx: "auto",
                        my: "auto"
                    }}
                >

                    <DialogTitle
                        sx={{
                            p: '1rem 1.5rem',
                            textAlign: 'center',
                        }}>
                        <Typography variant="h6">
                            Emitir Nota Fiscal de Serviço (NFS-e)
                        </Typography>
                    </DialogTitle>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
                        {errorMessage && (
                            <ErrorBox
                                errorMessage={errorMessage}
                                onClose={() => {
                                    setfiscalApiError(null);
                                    setSendFiscalDataError(null);
                                }}
                            />
                        )}
                        <Box>
                            <Typography variant="h7"> Cliente (Tomador) </Typography>
                            {loadingInvoiceData ? <Skeleton width={80} /> : <Typography>{invoice?.customer.name}</Typography>}
                        </Box>
                        <Box>
                            <Typography variant="h7"> Discriminação do serviço <Typography variant='caption' sx={{ color: 'error.main' }}><b>*</b></Typography></Typography>
                            <TextField
                                multiline
                                maxRows={10}
                                minRows={3}
                                fullWidth
                                placeholder="Digitar texto"
                                value={serviceDescriptionValue}
                                onChange={(e) => setServiceDescriptionValue(e.target.value)}
                                slotProps={{
                                    input: { maxLength: 1000 },
                                    formHelperText: {
                                        sx: { textAlign: "right" },
                                    },
                                }}
                                error={validation.serviceDescriptionLimit}
                                helperText={`${serviceDescriptionValue.length}/${1000}`}
                            />
                        </Box>
                        <Box>
                            <Box>
                                <Typography variant="h7"> Valor do serviço <Typography variant='caption' sx={{ color: 'error.main' }}><b>*</b></Typography></Typography>
                            </Box>
                            <CurrencyTextField
                                currencySymbol="R$"
                                variant="outlined"
                                outputFormat="number"
                                decimalCharacter=","
                                digitGroupSeparator="."
                                placeholder="0,00"
                                value={serviceValue}
                                onChange={(event, value) => setServiceValue(value)}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: '1.5rem' }}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="h7"> Desconto incondicionado </Typography>
                                    <TooltipUnconditionalDiscount />
                                </Box>
                                <CurrencyTextField
                                    currencySymbol="R$"
                                    variant="outlined"
                                    outputFormat="number"
                                    decimalCharacter=","
                                    digitGroupSeparator="."
                                    placeholder="0,00"
                                    value={unconditionalDiscountValue}
                                    onChange={(event, value) => setUnconditionalDiscountValue(value)}
                                />
                            </Box>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography variant="h7"> Desconto condicionado </Typography>
                                    <TooltipConditionalDiscount />
                                </Box>
                                <CurrencyTextField
                                    currencySymbol="R$"
                                    variant="outlined"
                                    outputFormat="number"
                                    decimalCharacter=","
                                    digitGroupSeparator="."
                                    placeholder="0,00"
                                    value={conditionalDiscountValue}
                                    onChange={(event, value) => setConditionalDiscountValue(value)}
                                />
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="h7"> Configuração fiscal do serviço <Typography variant='caption' sx={{ color: 'error.main' }}><b>*</b></Typography></Typography>
                            <Typography> {fiscalConfigData?.name || '-'} </Typography>
                        </Box>
                        <Box>
                            <Typography variant="h7"> Observações adicionais </Typography>
                            <TextField
                                multiline
                                maxRows={10}
                                minRows={1}
                                fullWidth
                                placeholder="Digitar texto"
                                value={observationsValue}
                                onChange={(e) => setObservationsValue(e.target.value)}
                                slotProps={{
                                    input: { maxLength: 1000 },
                                    formHelperText: {
                                        sx: { textAlign: "right" },
                                    },
                                }}
                                error={validation.observationsLimit}
                                helperText={`${observationsValue.length}/1000`}
                            />
                        </Box>
                        <Accordion
                            disableGutters
                            sx={{
                                backgroundColor: "transparent",
                                boxShadow: "none",
                                borderTop: "1px solid",
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                "&:before": {
                                    display: "none",
                                },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    px: 0,
                                    minHeight: "48px",
                                    "& .MuiAccordionSummary-content": {
                                        margin: 0,
                                    },
                                }}
                            >
                                <Typography variant="body1"> <b>Detalhes de Tributação e Alíquotas</b></Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 0, pt: 1, pb: 0 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2rem' }} >
                                    <Box>
                                        <Typography variant="body1">Os valores abaixo são um resumo da configuração fiscal aplicada.</Typography>
                                        <Typography
                                            variant="body1"
                                            onClick={() => window.open("/fiscal/settings", "_blank")}
                                            sx={{
                                                color: "success.main",
                                                textDecoration: "underline",
                                                cursor: "pointer",
                                            }}
                                        >
                                            Acessar as Configurações fiscais de Serviço (NFS-e)
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <FiscalInfosTable fiscalData={fiscalConfigData} />
                                    </Box>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                        <Box>
                            <Typography variant='caption' sx={{ color: 'error.main' }}><b>*</b> Indica um campo obrigatório </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ display: 'flex' }}>
                        <Button
                            variant="cancelDialog"
                            color="secondary"
                            onClick={modalOnClose}
                        >
                            Fechar
                        </Button>
                        <Tooltip title={submitTooltip} placement="top" arrow>
                            <span>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    loadingPosition="start"
                                    loading={loadingSendData}
                                    disabled={isSubmitDisabled}
                                    onClick={handleSendData}
                                >
                                    Confirmar
                                </Button>
                            </span>
                        </Tooltip>
                    </DialogActions>
                </Paper>
            </Modal>
        </>
    )
}

export default NFSeModal;